import { Component, h, Prop, Element, State, type EventEmitter, Event } from '@stencil/core';
import type {
  SearchApiDocumentMySE
} from '../../../../api/SearchApi';
import {type SearchTableConfiguration, SearchTableTheme, type SortableHeader, SortOrder, type TableDeletedActionClicked } from './SearchTableConfiguration';
import { SearchTableHeaders } from './subcomponents/search-table-header';
import { SearchTableColumn } from './subcomponents/search-table-column';
import { makeLogger } from '../../../../logger/logger';
import type { ReflectedTab } from '../../../SearchCompletedEvent';
import spinner from '@se/icons/svg/spinner.svg';
import type { labelProviderInterface } from '../../../LabelProviderInterface';

const { info, warn, err } = makeLogger({
  pathToCurrentFile: 'components/result-page/search-tab/tabular/search-table.tsx',
  loggersList: ['warn', 'info', 'err']
});

// THIS COMPONENT IS UNDER DEVELOPMENT
@Component({
  tag: 'uiaas-search-table',
  shadow: false,
  styleUrl: 'search-table.scss',
})
export class SearchTable {
  @Prop({mutable: true}) config: SearchTableConfiguration;

  private sortHeader: string;
 @State() sortTableHeader: Array<SortableHeader> = [];
  @Element() el;

  @Prop({ mutable: true }) labelProvider?: labelProviderInterface;

  @Prop() readonly documents: SearchApiDocumentMySE[];

  @Prop() readonly query: string;

  @Prop() readonly reflectedState: ReflectedTab;

  @Prop({ attribute: 'table-configuration' }) readonly tableConfiguration?: SearchTableConfiguration | string;
  private parseTableConfiguration(newValue, oldValue = null, trigger = null) : void{
      try {
        this.config = JSON.parse(newValue) as SearchTableConfiguration;
      } catch (exception) {
        err(exception, 'Couldn\'t parse search table configuration as JSON', {
          this: this,
          newValue: newValue,
          oldValue: oldValue,
          trigger: trigger
        });
      }
  }

  @Event({ eventName: 'search:tableDeletedActionClicked' }) tableDeletedActionClicked: EventEmitter<TableDeletedActionClicked>;

  componentWillLoad() {
      if (!this.labelProvider) {
        info(
          'Assigning default labelProvider to SearchTable component'
        );
        this.labelProvider = (name, fallback) => {
            warn(
              'LabelProvider not provided in SearchTable component',
              [name, fallback]
            );
            return fallback;
        };
      }

      this.parseTableConfiguration(this.tableConfiguration);
  }

  render(): HTMLElement {
      return (
        <div class={`search-table ${this.config.theme}`}>
          <table class={'search'} cellspacing="0">
            <thead>
              <SearchTableHeaders
                columns={this.config.columns}
                labelProvider={this.labelProvider}
                sortColumn={(fieldName) => this.sortColumn(fieldName)}
                sortTableHeader={this.sortTableHeader}
                theme={this.config.theme}/>
            </thead>
            <tbody>
              {this.documents.map(doc => {
                  return <SearchTableColumn
                    labelProvider={this.labelProvider}
                    columns={this.config.columns}
                    doc={doc}
                    tableDeletedActionClicked={(idFieldName, linkUrl) => this.tableDeletedActionClicked.emit({id: idFieldName, url: linkUrl})}
                  />;
              })}
            </tbody>
          </table>
          {this.documents.length === 0 ?
            this.reflectedState.ajaxIsLoading ?
              <div class="search-tab-loading__container">
                <div
                  class="search-tab-loading-icon"
                  innerHTML={spinner}
                ></div>
              </div>
              :
              <uiaas-search-no-results
                query={this.query}
                theme={this.config.theme as SearchTableTheme}
                labelProvider={(e, f) => this.labelProvider(e, f)}
              />
            : ''
          }
        </div>
      );
  }

  private sortColumn(fieldName: string): void{
      const existingSortTableHeader = this.sortTableHeader.find(x => x.fieldName === fieldName);
      if(existingSortTableHeader)
      {
        existingSortTableHeader.sortOrder = existingSortTableHeader.sortOrder === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending;
        this.sortTableHeader = [
          ...this.sortTableHeader
        ];
      }else{
        const sortTableHeader: SortableHeader = {
          fieldName: fieldName,
          sortOrder: SortOrder.Ascending
        };
        this.sortTableHeader = [
          sortTableHeader
        ];
      }
  }
}
